/**
 * Labstep
 *
 * @module components/File/Action/Annotate
 * @desc Annotate a file
 */

import { Can } from 'labstep-web/components/Entity/Can';
import { Action } from 'labstep-web/components/Entity/Can/types';
import { ThumbnailContainer } from 'labstep-web/containers/File/Thumbnail';
import { FileUploadContainer } from 'labstep-web/containers/File/Upload';
import { Loadable } from 'labstep-web/core/Loadable';
import React from 'react';
import type { FileActionAnnotateProps } from './types';

export const ImageAnnotation = React.lazy(() =>
  import('labstep-web/core/ImageAnnotation').then((module) => ({
    default: module.ImageAnnotation,
  })),
);

export const FileActionAnnotate: React.FC<
  FileActionAnnotateProps
> = ({ file, viewComponent }) =>
  file.isImage ? (
    <Can
      entityName={file.entityName}
      id={file.id}
      action={Action.edit}
      field={['annotated_file', 'image_annotation_data']}
    >
      <FileUploadContainer
        entityType={file.entityName}
        entityId={file.id}
      >
        {({ uploadFile }) => (
          <Loadable loader={<span />}>
            <ThumbnailContainer
              file={file}
              params={{ w: 1300 }}
              customFetching
            >
              {({ src, status, fetchUrl }) => (
                <ImageAnnotation
                  // Key needed for https://github.com/Labstep/web/issues/6734
                  key={file.id}
                  src={src}
                  viewComponent={viewComponent}
                  annotationData={
                    file.image_annotation &&
                    file.image_annotation.image_annotation_data
                  }
                  loadingStatus={status}
                  onConfirm={(output, options) => {
                    const outputFile = new File(
                      [output.file],
                      file.name,
                    );
                    uploadFile(
                      {
                        file: outputFile,
                        additionalBody: {
                          annotated_file_id: file.id,
                          image_annotation_data: JSON.stringify(
                            output.data,
                          ),
                        },
                      },
                      { ...options, toast: false },
                    );
                  }}
                  fetchUrl={fetchUrl}
                />
              )}
            </ThumbnailContainer>
          </Loadable>
        )}
      </FileUploadContainer>
    </Can>
  ) : null;
